import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Search, CreditCard, Calendar, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { trpc } from '@/utils/trpc'
import { useNavigate } from '@tanstack/react-router'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp"

export const Route = createFileRoute('/subscriptions')({
  component: Subscriptions,
})

function Subscriptions() {
  const [email, setEmail] = useState('')
  const [searchEmail, setSearchEmail] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const navigate = useNavigate()

  const sendVerificationCode = useMutation(trpc.donation.sendVerificationCode.mutationOptions({
    onSuccess: () => {
      setIsVerifying(true)
    },
    onError: (error) => {
      setError(error.message)
    },
  }))

  const verifyEmail = useMutation(trpc.donation.verifyEmail.mutationOptions({
    onSuccess: (data) => {
      setToken(data.token)
      setSearchEmail(email)
    },
    onError: (error) => {
      setError(error.message)
    },
  }))

  const [error, setError] = useState('')

  const subscriptions = useQuery({
    ...trpc.donation.getSubscriptionsByEmail.queryOptions({
      email: searchEmail,
      token: token || '',
    }),
    enabled: !!searchEmail && !!token,
  })

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    sendVerificationCode.mutate({ email })
  }

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    verifyEmail.mutate({ email, code: verificationCode })
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto max-w-2xl p-6"
    >
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
        >
          <Search className="w-20 h-20 text-green-500 mx-auto mb-4" />
        </motion.div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Hitta Dina Gåvor</h1>
        <p className="text-gray-600">Sök efter dina månadsvisa gåvor med din e-postadress</p>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-lg p-6 mb-8"
      >
        {!token ? (
          <>
            {!isVerifying ? (
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="flex gap-4">
                  <Input
                    type="email"
                    placeholder="Ange din e-postadress"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" disabled={sendVerificationCode.isPending}>
                    {sendVerificationCode.isPending ? 'Skickar...' : 'Sök'}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600 mb-4 text-center">
                    Vi har skickat en verifieringskod till {email}
                  </p>
                  <form onSubmit={handleVerification} className="space-y-4">
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={6}
                        value={verificationCode}
                        onChange={(value) => setVerificationCode(value)}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    <div className="flex justify-center gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsVerifying(false)}
                      >
                        Tillbaka
                      </Button>
                      <Button
                        type="submit"
                        disabled={verificationCode.length !== 6 || verifyEmail.isPending}
                      >
                        {verifyEmail.isPending ? 'Verifierar...' : 'Verifiera'}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </>
        ) : null}

        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-red-50 text-red-800 rounded-xl flex items-center"
          >
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </motion.div>
        )}

        {subscriptions.isLoading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
          </div>
        )}

        {subscriptions.isError && (
          <div className="mt-4 p-4 bg-red-50 text-red-800 rounded-xl flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>Ett fel uppstod vid sökning</span>
          </div>
        )}

        {subscriptions.data && subscriptions.data.length === 0 && searchEmail && (
          <div className="mt-4 p-4 bg-yellow-50 text-yellow-800 rounded-xl text-center">
            Inga aktiva gåvor hittades för denna e-postadress
          </div>
        )}

        {subscriptions.data && subscriptions.data.length > 0 && (
          <div className="mt-6 space-y-4">
            {subscriptions.data.map((subscription) => (
              <motion.div
                key={subscription.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-gray-50 rounded-xl"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <CreditCard className={`w-5 h-5 ${subscription.status === 'active' ? 'text-green-500' : 'text-gray-400'} mr-2`} />
                    <div>
                      <span className="font-semibold">
                        {subscription.amount} {subscription.currency.toUpperCase()}/månad
                      </span>
                      {subscription.status !== 'active' && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Avslutad
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate({ to: `/subscription/${subscription.id}` })}
                    className="text-sm"
                  >
                    Visa Detaljer
                  </Button>
                </div>
                {subscription.status === 'active' && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>
                      Nästa betalning: {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString('sv-SE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-center"
      >
        <Button
          variant="outline"
          onClick={() => navigate({ to: '/' })}
          className="hover:bg-green-50 hover:text-green-600 hover:border-green-200 transition-colors"
        >
          Tillbaka till Startsidan
        </Button>
      </motion.div>
    </motion.div>
  )
} 